import { Stack, Alert, Text, Box, Table, Group, Code, TextInput, Select } from "@mantine/core";
import { Info, GripVertical } from "lucide-react";
import { Reorder } from "framer-motion";
import { UseFormReturnType } from "@mantine/form";
import type { ReportColumn } from "@bi-studio/shared/lib";

interface ReportColumnsConfigProps {
    form: UseFormReturnType<any>;
}

export function ReportColumnsConfig({ form }: ReportColumnsConfigProps) {
    const columns: ReportColumn[] = form.values.columns || [];

    return (
        <Stack gap="md">
            <Alert icon={<Info size={16} />} title="Column Settings" color="gray" variant="light" radius="md">
                Configure how columns are displayed and their data types. Columns are automatically detected when you preview the query.
            </Alert>

            {columns.length === 0 ? (
                <Text c="dimmed" fs="italic" ta="center" py="xl">
                    No columns detected. Run a preview to detect columns.
                </Text>
            ) : (
                <Box style={{ 
                    borderRadius: 'var(--mantine-radius-md)', 
                    border: '1px solid var(--modal-border)', 
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch'
                }}>
                    <Table striped highlightOnHover verticalSpacing="sm" style={{ minWidth: 500 }}>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th w={40}></Table.Th>
                                <Table.Th w={150}>Name</Table.Th>
                                <Table.Th style={{ minWidth: 150 }}>Display Label</Table.Th>
                                <Table.Th w={120}>Type</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Reorder.Group
                            as="tbody"
                            axis="y"
                            values={columns}
                            onReorder={(newOrder: ReportColumn[]) => {
                                const updated = newOrder.map((col, idx) => ({ ...col, order: idx }));
                                form.setFieldValue('columns', updated);
                            }}
                            className="mantine-Table-tbody"
                        >
                            {columns.map((col, idx) => (
                                <Reorder.Item
                                    key={col.name}
                                    value={col}
                                    as="tr"
                                    className="mantine-Table-tr"
                                >
                                    <Table.Td>
                                        <Group gap={0} wrap="nowrap" style={{ cursor: 'grab' }}>
                                            <GripVertical size={16} color="gray" />
                                        </Group>
                                    </Table.Td>
                                    <Table.Td>
                                        <Code>{col.name}</Code>
                                    </Table.Td>
                                    <Table.Td>
                                        <TextInput
                                            size="xs"
                                            value={col.displayName}
                                            onChange={(e) => {
                                                form.setFieldValue(`columns.${idx}.displayName`, e.target.value);
                                            }}
                                        />
                                    </Table.Td>
                                    <Table.Td>
                                        <Select
                                            size="xs"
                                            value={col.dataType}
                                            onChange={(val) => {
                                                form.setFieldValue(`columns.${idx}.dataType`, val || 'string');
                                            }}
                                            data={[
                                                { value: 'string', label: 'String' },
                                                { value: 'numeric', label: 'Numeric' },
                                                { value: 'date', label: 'Date' },
                                                { value: 'boolean', label: 'Boolean' },
                                                { value: 'json', label: 'JSON' },
                                            ]}
                                        />
                                    </Table.Td>
                                </Reorder.Item>
                            ))}
                        </Reorder.Group>
                    </Table>
                </Box>
            )}
        </Stack>
    );
}
